import React from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../lib/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { useI18n } from "../lib/i18n";
import { PARENT } from "../constants/testIds";
import { UserPlus, GraduationCap, CalendarCheck, Award, Megaphone, ChevronRight, Trash2, Sparkles, MessageSquare, BookOpen } from "lucide-react";
import SubscriptionCard from "../components/SubscriptionCard";
import { AttachmentList } from "../components/Attachments";
import MessagesPage from "./MessagesPage";
import { ParentHomework } from "../components/Homework";
import { ParentConferences, ConferenceNotice } from "../components/ParentConferences";

const GRADES = ["PreK", "K", "1", "2", "3", "4", "5", "6", "7", "8", "9", "10", "11", "12", "Quran", "Arabic"];

export default function ParentDashboard() {
    const { user } = useAuth();
    const { t } = useI18n();
    const navigate = useNavigate();
    const [family, setFamily] = React.useState({ students: [] });
    const [tab, setTab] = React.useState("family");
    const [enrollments, setEnrollments] = React.useState([]);
    const [announcements, setAnnouncements] = React.useState([]);
    const [selected, setSelected] = React.useState([]);
    const [quote, setQuote] = React.useState(null);
    const [dialogOpen, setDialogOpen] = React.useState(false);

    const load = React.useCallback(async () => {
        const [f, e, a] = await Promise.all([
            api.get("/family/me"),
            api.get("/enrollments/me"),
            api.get("/announcements"),
        ]);
        setFamily(f.data);
        setEnrollments(e.data);
        setAnnouncements(a.data);
    }, []);

    React.useEffect(() => { load(); }, [load]);

    React.useEffect(() => {
        if (selected.length === 0) { setQuote(null); return; }
        api.post("/enrollments/quote", { student_ids: selected }).then(r => setQuote(r.data)).catch(() => setQuote(null));
    }, [selected]);

    async function startRegistration() {
        try {
            const { data } = await api.post("/enrollments", { student_ids: selected });
            toast.success("Registration created");
            navigate(`/checkout/${data.id}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to start registration");
        }
    }

    return (
        <main className="py-12 min-h-[calc(100vh-5rem)]">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <div className="flex items-baseline justify-between flex-wrap gap-3 mb-8">
                    <div>
                        <div className="overline text-brand-stone">Family dashboard</div>
                        <h1 className="font-serif text-4xl text-brand-ink">As-salamu alaykum, {user?.full_name?.split(" ")[0]}</h1>
                    </div>
                    <AddChildDialog open={dialogOpen} onOpenChange={setDialogOpen} onDone={load} />
                </div>

                <Tabs value={tab} onValueChange={setTab}>
                    <ConferenceNotice onOpen={() => setTab("conf")} />
                    <TabsList className="mb-8 flex-wrap h-auto">
                        <TabsTrigger value="family" data-testid={PARENT.familyTab}>{t("tab_family")}</TabsTrigger>
                        <TabsTrigger value="tuition" data-testid={PARENT.tuitionTab}>{t("tab_tuition")}</TabsTrigger>
                        <TabsTrigger value="attendance" data-testid={PARENT.attendanceTab}>{t("tab_attendance")}</TabsTrigger>
                        <TabsTrigger value="reports" data-testid={PARENT.reportCardsTab}>{t("tab_report_cards")}</TabsTrigger>
                        <TabsTrigger value="hw" data-testid="parent-homework-tab">Homework</TabsTrigger>
                        <TabsTrigger value="ann" data-testid={PARENT.announcementsTab}>{t("tab_announcements")}</TabsTrigger>
                        <TabsTrigger value="msg" data-testid="parent-messages-tab">Messages</TabsTrigger>
                        <TabsTrigger value="conf" data-testid="parent-conferences-tab">Conferences</TabsTrigger>
                    </TabsList>

                    <TabsContent value="family">
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {family.students.map((s) => (
                                <Card key={s.id} className="card-lift">
                                    <CardContent className="p-6">
                                        <div className="flex items-start justify-between">
                                            <div className="w-12 h-12 rounded-full bg-primary/10 text-primary grid place-items-center font-dash text-lg font-semibold">
                                                {s.first_name[0]}{s.last_name[0]}
                                            </div>
                                            <Badge className="bg-accent/10 text-accent border-0">Grade {s.grade}</Badge>
                                        </div>
                                        <h3 className="mt-4 font-dash text-lg font-semibold">{s.first_name} {s.last_name}</h3>
                                        <div className="text-sm text-brand-stone mt-1">DOB {s.date_of_birth} · {s.gender}</div>
                                        {s.assigned_class && (
                                            <div className="mt-4 p-3 rounded-lg bg-primary/5 border border-primary/15">
                                                <div className="overline text-primary/80 text-[10px]">Assigned class</div>
                                                <div className="font-dash text-sm font-semibold text-primary mt-0.5">{s.assigned_class.class_name}</div>
                                                <div className="text-xs text-brand-stone mt-0.5">
                                                    {s.assigned_class.teacher_name || "Teacher pending"} · {s.assigned_class.room}
                                                </div>
                                            </div>
                                        )}
                                        <div className="mt-4 pt-4 border-t border-border/60 flex items-center gap-3">
                                            <Checkbox
                                                id={`sel-${s.id}`}
                                                checked={selected.includes(s.id)}
                                                onCheckedChange={(v) => setSelected(v ? [...selected, s.id] : selected.filter(x => x !== s.id))}
                                            />
                                            <label htmlFor={`sel-${s.id}`} className="text-sm text-brand-ink cursor-pointer">
                                                Include in registration
                                            </label>
                                            <button className="ml-auto text-brand-stone hover:text-destructive"
                                                onClick={async () => {
                                                    if (window.confirm(`Remove ${s.first_name}?`)) {
                                                        await api.delete(`/students/${s.id}`);
                                                        load();
                                                    }
                                                }}>
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                            {family.students.length === 0 && (
                                <Card className="md:col-span-2 lg:col-span-3 border-dashed">
                                    <CardContent className="p-10 text-center">
                                        <UserPlus className="w-8 h-8 mx-auto text-brand-stone" />
                                        <div className="mt-3 font-dash text-lg">No children added yet</div>
                                        <p className="text-sm text-brand-stone mt-1">Add your child to begin enrollment.</p>
                                    </CardContent>
                                </Card>
                            )}
                        </div>
                        {quote && (
                            <Card className="mt-8 border-primary/20 bg-primary/5">
                                <CardContent className="p-6 flex flex-wrap items-center justify-between gap-4">
                                    <div>
                                        <div className="overline text-primary">Ready to register</div>
                                        <div className="text-lg font-dash mt-1">{quote.child_count} child{quote.child_count > 1 ? "ren" : ""} · Total ${(quote.total_cents / 100).toFixed(2)} USD</div>
                                        <div className="text-xs text-brand-stone mt-1">$15 registration + $60 first month per child · $60/month begins in 30 days</div>
                                    </div>
                                    <Button size="lg" onClick={startRegistration}
                                        data-testid={PARENT.registerChildrenBtn}
                                        className="bg-primary hover:bg-primary/90 gap-2">
                                        Continue to payment <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>

                    <TabsContent value="tuition">
                        <div className="mb-6">
                            <SubscriptionCard />
                        </div>
                        <div className="space-y-3">
                            <div className="overline text-brand-stone">Past invoices</div>
                            {enrollments.map(e => (
                                <Card key={e.id}>
                                    <CardContent className="p-6 flex items-center justify-between flex-wrap gap-4">
                                        <div>
                                            <div className="overline text-brand-stone">Invoice</div>
                                            <div className="font-dash text-lg">{e.student_ids.length} child{e.student_ids.length > 1 ? "ren" : ""} · ${(e.amount_cents / 100).toFixed(2)}</div>
                                            <div className="text-xs text-brand-stone mt-1">{new Date(e.created_at).toLocaleDateString()}</div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <Badge className={e.status === "PAID" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}>{e.status}</Badge>
                                            {e.status !== "PAID" && (
                                                <Button data-testid={PARENT.checkoutBtn} onClick={() => navigate(`/checkout/${e.id}`)} className="bg-primary hover:bg-primary/90">Pay now</Button>
                                            )}
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                            {enrollments.length === 0 && <div className="text-brand-stone text-center py-16">No invoices yet.</div>}
                        </div>
                    </TabsContent>

                    <TabsContent value="attendance"><AttendanceView students={family.students} /></TabsContent>
                    <TabsContent value="reports"><ReportsView students={family.students} /></TabsContent>
                    <TabsContent value="hw"><ParentHomework /></TabsContent>
                    <TabsContent value="conf"><ParentConferences /></TabsContent>

                    <TabsContent value="ann">
                        <div className="space-y-3">
                            {announcements.map(a => (
                                <Card key={a.id}><CardContent className="p-6">
                                    <div className="flex items-center gap-2 mb-1">
                                        <Megaphone className="w-4 h-4 text-primary" />
                                        <div className="overline text-brand-stone">{new Date(a.created_at).toLocaleDateString()}</div>
                                    </div>
                                    <h3 className="font-dash text-lg font-semibold">{a.title}</h3>
                                    <p className="text-brand-stone mt-1 whitespace-pre-wrap">{a.body}</p>
                                    <AttachmentList items={a.attachments} testid={`parent-ann-att-${a.id}`} />
                                </CardContent></Card>
                            ))}
                            {announcements.length === 0 && <div className="text-brand-stone text-center py-16">No announcements yet.</div>}
                        </div>
                    </TabsContent>

                    <TabsContent value="msg">
                        <MessagesPage />
                    </TabsContent>
                </Tabs>
            </div>
        </main>
    );
}

function AddChildDialog({ open, onOpenChange, onDone }) {
    const navigate = useNavigate();
    const [form, setForm] = React.useState({
        first_name: "", last_name: "", date_of_birth: "", grade: "PreK", gender: "male",
        allergies: "", emergency_contact: "", emergency_phone: "",
    });
    const [suggested, setSuggested] = React.useState(null);
    const [confirmation, setConfirmation] = React.useState(null); // {registration, first_child_ever, student_name}
    const [submitting, setSubmitting] = React.useState(false);
    const [retroDates, setRetroDates] = React.useState([]); // approved backdated Sundays from /config
    const [joinedOn, setJoinedOn] = React.useState("current"); // "current" | ISO date

    React.useEffect(() => {
        api.get("/config").then(r => setRetroDates(r.data.retro_join_dates || [])).catch(() => setRetroDates([]));
    }, []);

    React.useEffect(() => {
        if (!form.date_of_birth) { setSuggested(null); return; }
        api.get("/grade-suggestion", { params: { dob: form.date_of_birth } })
            .then(r => {
                setSuggested(r.data.grade);
                setForm(f => ({ ...f, grade: r.data.grade }));
            }).catch(() => setSuggested(null));
    }, [form.date_of_birth]);

    async function submit(e) {
        e.preventDefault();
        if (submitting) return;
        setSubmitting(true);
        try {
            const payload = { ...form };
            if (joinedOn !== "current") payload.joined_on = joinedOn;
            const { data } = await api.post("/students/register-and-invoice", payload);
            // Show confirmation modal with prorated invoice details, then redirect to checkout.
            setConfirmation({
                registration: data.registration,
                first_child_ever: data.first_child_ever,
                registration_fee_cents: data.registration_fee_cents,
                student_name: form.first_name,
                assigned_class: data.student?.assigned_class,
            });
            onOpenChange(false);
            setForm({ ...form, first_name: "", last_name: "", date_of_birth: "" });
            setJoinedOn("current");
            onDone();
        } catch (err) {
            toast.error(err.response?.data?.detail || "Failed to add child");
        } finally { setSubmitting(false); }
    }

    function formatRetroLabel(iso) {
        // "2026-09-13" → "September 13, 2026"
        const [y, m, d] = iso.split("-").map(Number);
        const dt = new Date(Date.UTC(y, m - 1, d));
        return dt.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric", timeZone: "UTC" });
    }

    return (
        <>
            <Dialog open={open} onOpenChange={onOpenChange}>
                <DialogTrigger asChild>
                    <Button data-testid={PARENT.addChildBtn} className="bg-primary hover:bg-primary/90 gap-2">
                        <UserPlus className="w-4 h-4" /> Add child
                    </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader><DialogTitle className="font-serif text-2xl">Add a child</DialogTitle></DialogHeader>
                    <form onSubmit={submit} className="space-y-4" data-testid={PARENT.childForm}>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>First name</Label>
                            <Input required placeholder="Zayd" value={form.first_name} onChange={e => setForm({ ...form, first_name: e.target.value })} data-testid={PARENT.firstName} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Last name</Label>
                            <Input required value={form.last_name} onChange={e => setForm({ ...form, last_name: e.target.value })} data-testid={PARENT.lastName} className="mt-1.5" />
                        </div>
                    </div>
                    <div>
                        <Label>Date of birth</Label>
                        <Input type="date" required value={form.date_of_birth} onChange={e => setForm({ ...form, date_of_birth: e.target.value })} data-testid={PARENT.dob} className="mt-1.5" />
                        {suggested && (
                            <div className="mt-2 flex items-center gap-2 text-xs text-primary">
                                <Sparkles className="w-3 h-3" />
                                Suggested grade based on age: <strong>{suggested}</strong>
                            </div>
                        )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Grade</Label>
                            <Select value={form.grade} onValueChange={(v) => setForm({ ...form, grade: v })}>
                                <SelectTrigger data-testid={PARENT.grade} className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    {GRADES.map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <Label>Gender</Label>
                            <Select value={form.gender} onValueChange={(v) => setForm({ ...form, gender: v })}>
                                <SelectTrigger data-testid={PARENT.gender} className="mt-1.5"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="male">Male</SelectItem>
                                    <SelectItem value="female">Female</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    {retroDates.length > 0 && (
                        <div className="rounded-lg border border-primary/20 bg-primary/5 p-3" data-testid="retro-join-block">
                            <Label className="text-primary font-medium">Did your child start attending on an earlier Sunday?</Label>
                            <p className="text-[11px] text-brand-stone mt-1">
                                If so, we'll backdate your first invoice to that Sunday so you're only charged for the sessions you actually attended.
                            </p>
                            <Select value={joinedOn} onValueChange={setJoinedOn}>
                                <SelectTrigger data-testid="retro-join-select" className="mt-2 bg-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="current" data-testid="retro-join-current">Registering today (prorate normally)</SelectItem>
                                    {retroDates.map(d => (
                                        <SelectItem key={d} value={d} data-testid={`retro-join-${d}`}>
                                            Started on {formatRetroLabel(d)}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                    )}
                    <div>
                        <Label>Allergies / notes</Label>
                        <Input placeholder="e.g. Peanut allergy, asthma inhaler in bag" value={form.allergies} onChange={e => setForm({ ...form, allergies: e.target.value })} className="mt-1.5" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <Label>Emergency contact</Label>
                            <Input placeholder="e.g. Uncle Omar (grandparent)" value={form.emergency_contact} onChange={e => setForm({ ...form, emergency_contact: e.target.value })} className="mt-1.5" />
                        </div>
                        <div>
                            <Label>Emergency phone</Label>
                            <Input placeholder="(253) 555-0000" value={form.emergency_phone} onChange={e => setForm({ ...form, emergency_phone: e.target.value })} className="mt-1.5" />
                        </div>
                    </div>
                    <Button type="submit" disabled={submitting} data-testid={PARENT.submitChild} className="w-full bg-primary hover:bg-primary/90">{submitting ? "Saving…" : "Save & continue to payment"}</Button>
                </form>
            </DialogContent>
        </Dialog>
        <Dialog open={!!confirmation} onOpenChange={o => { if (!o) setConfirmation(null); }}>
            <DialogContent data-testid="prorated-confirmation-dialog">
                <DialogHeader><DialogTitle className="font-serif text-2xl">Child added ✓</DialogTitle></DialogHeader>
                {confirmation && (
                    <div className="space-y-3">
                        <div className="text-sm">
                            <b>{confirmation.student_name}</b> has been added to your family.
                            {confirmation.assigned_class && (
                                <span> Placed in <b>{confirmation.assigned_class.class_name}</b>{confirmation.assigned_class.teacher_name ? ` with ${confirmation.assigned_class.teacher_name}` : ""}.</span>
                            )}
                        </div>
                        <Card className="border-primary/20 bg-primary/5">
                            <CardContent className="p-4 space-y-2">
                                <div className="overline text-primary">Your first invoice</div>
                                {confirmation.registration.line_items?.map((li, i) => (
                                    <div key={i} className="flex items-center justify-between text-sm">
                                        <span>{li.label}</span>
                                        <span>${(li.amount_cents / 100).toFixed(2)}</span>
                                    </div>
                                ))}
                                <div className="border-t border-primary/20 pt-2 flex items-center justify-between font-dash">
                                    <span>Total</span>
                                    <span>${(confirmation.registration.amount_cents / 100).toFixed(2)}</span>
                                </div>
                                <div className="text-[11px] text-brand-stone italic">
                                    {confirmation.registration.retro_join_date
                                        ? <>Backdated to <b>{formatRetroLabel(confirmation.registration.retro_join_date)}</b> so you only pay for the Sundays your child attended.</>
                                        : confirmation.registration.covers_next_month
                                            ? <>This is <b>{confirmation.registration.covers_month_label}</b> tuition — you registered after the last Sunday of this month, so the class Sundays remaining are next month's.</>
                                            : <>Prorated based on today (day {confirmation.registration.prorated_day} of the month).</>
                                    }
                                    {confirmation.registration_fee_cents > 0 ? " Includes the $15 registration fee for this child." : ""}
                                </div>
                            </CardContent>
                        </Card>
                        <Button
                            data-testid="go-to-payment-btn"
                            className="w-full bg-primary hover:bg-primary/90"
                            onClick={() => {
                                const rid = confirmation.registration.id;
                                setConfirmation(null);
                                navigate(`/checkout/${rid}`);
                            }}
                        >
                            Continue to payment
                        </Button>
                    </div>
                )}
            </DialogContent>
        </Dialog>
        </>
    );
}

function AttendanceView({ students }) {
    const [data, setData] = React.useState({});
    React.useEffect(() => {
        (async () => {
            const out = {};
            for (const s of students) {
                try { out[s.id] = (await api.get(`/attendance/student/${s.id}`)).data; }
                catch { out[s.id] = []; }
            }
            setData(out);
        })();
    }, [students]);

    function stats(rows = []) {
        const total = rows.length;
        const present = rows.filter(r => r.status === "present").length;
        const late = rows.filter(r => r.status === "late").length;
        const absent = rows.filter(r => r.status === "absent").length;
        const excused = rows.filter(r => r.status === "excused").length;
        const percent = total ? Math.round(((present + late) / total) * 100) : 0;
        return { total, present, late, absent, excused, percent };
    }

    return (
        <div className="space-y-6">
            {students.map(s => {
                const rows = data[s.id] || [];
                const st = stats(rows);
                return (
                    <Card key={s.id} data-testid={`parent-attendance-card-${s.id}`}>
                        <CardContent className="p-6">
                            <div className="flex items-center gap-3 mb-4 flex-wrap">
                                <CalendarCheck className="w-4 h-4 text-primary" />
                                <div className="font-dash text-lg font-semibold">{s.first_name} {s.last_name}</div>
                                <Badge variant="secondary">Grade {s.grade}</Badge>
                                {st.total > 0 && (
                                    <div className="ml-auto flex items-center gap-3">
                                        <div className="w-28 h-2 rounded-full bg-border overflow-hidden">
                                            <div
                                                className={`h-full ${st.percent >= 90 ? "bg-emerald-500" : st.percent >= 75 ? "bg-amber-500" : "bg-destructive"}`}
                                                style={{ width: `${st.percent}%` }}
                                            />
                                        </div>
                                        <div className="font-dash font-semibold text-brand-ink">{st.percent}% attended</div>
                                    </div>
                                )}
                            </div>
                            {st.total > 0 && (
                                <div className="grid grid-cols-4 gap-3 mb-4">
                                    <StatPill label="Present" value={st.present} tone="text-emerald-700" />
                                    <StatPill label="Late" value={st.late} tone="text-amber-700" />
                                    <StatPill label="Absent" value={st.absent} tone="text-destructive" />
                                    <StatPill label="Excused" value={st.excused} tone="text-brand-stone" />
                                </div>
                            )}
                            {rows.length === 0 ? (
                                <div className="text-sm text-brand-stone">No attendance records yet.</div>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {rows.map((r, i) => (
                                        <div key={i} className="text-xs px-3 py-1.5 rounded-full border border-border">
                                            {r.date} · <span className={
                                                r.status === "present" ? "text-emerald-700" :
                                                    r.status === "absent" ? "text-destructive" : "text-amber-700"}>{r.status}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </CardContent>
                    </Card>
                );
            })}
            {students.length === 0 && <div className="text-brand-stone text-center py-16">Add children to see attendance.</div>}
        </div>
    );
}

function StatPill({ label, value, tone }) {
    return (
        <div className="rounded-lg border border-border p-3">
            <div className="text-xs text-brand-stone">{label}</div>
            <div className={`font-dash text-xl font-semibold ${tone}`}>{value}</div>
        </div>
    );
}

function ReportsView({ students }) {
    const [data, setData] = React.useState({});
    React.useEffect(() => {
        (async () => {
            const out = {};
            for (const s of students) {
                try { out[s.id] = (await api.get(`/report-cards/student/${s.id}`)).data; }
                catch { out[s.id] = []; }
            }
            setData(out);
        })();
    }, [students]);
    return (
        <div className="space-y-6">
            {students.map(s => (
                <Card key={s.id}>
                    <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <Award className="w-4 h-4 text-primary" />
                            <div className="font-dash text-lg font-semibold">{s.first_name} {s.last_name}</div>
                        </div>
                        {(data[s.id]?.length || 0) === 0 ? (
                            <div className="text-sm text-brand-stone">No report cards yet.</div>
                        ) : data[s.id].map(r => (
                            <div key={r.id} className="border-t border-border/60 pt-3 mt-3">
                                <div className="overline text-brand-stone">{r.term}</div>
                                <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
                                    {Object.entries(r.grades).map(([k, v]) => (
                                        <div key={k} className="rounded-lg border border-border p-3">
                                            <div className="text-xs text-brand-stone">{k}</div>
                                            <div className="font-dash text-lg font-semibold">{v}</div>
                                        </div>
                                    ))}
                                </div>
                                {r.comments && <p className="mt-3 text-sm text-brand-stone italic">&ldquo;{r.comments}&rdquo;</p>}
                            </div>
                        ))}
                    </CardContent>
                </Card>
            ))}
            {students.length === 0 && <div className="text-brand-stone text-center py-16">Add children to see report cards.</div>}
        </div>
    );
}
